/* ==========================================================================
   TIMBER RIDGE — MASTER OVERRIDES JS
   --------------------------------------------------------------------------
   Purpose:
   Centralized JS system for:
   - Symbol wrapping (™ / ®)
   - Collection-based content swapping (copy + images)
   - Shopify filter behavior override (single-select)
   - Safe re-render handling (AJAX + filters)

   Notes:
   - Scoped primarily to collection pages where needed
   - Uses shared utilities to avoid duplicate observers/hooks
   - Safe for Impulse theme dynamic rendering

   Last Updated: 2026-03-27
   ========================================================================== */


/* ==========================================================================
   GLOBAL UTILITIES
   - Shared helpers used across all modules
   ========================================================================== */
(function () {
  const DEBUG = false;

  function log(label, ...args) {
    if (DEBUG) console.log(`[TR ${label}]`, ...args);
  }

  function schedule(fn) {
    requestAnimationFrame(() => requestAnimationFrame(fn));
  }

  function onReady(fn) {
    document.addEventListener("DOMContentLoaded", () => schedule(fn));
    window.addEventListener("load", () => schedule(fn));
    window.addEventListener("popstate", () => schedule(fn));
  }

  function hookHistory(fn) {
    const push = history.pushState;
    history.pushState = function () {
      const ret = push.apply(this, arguments);
      schedule(fn);
      return ret;
    };

    const replace = history.replaceState;
    history.replaceState = function () {
      const ret = replace.apply(this, arguments);
      schedule(fn);
      return ret;
    };
  }

  function observeDOM(fn) {
    const mo = new MutationObserver(() => schedule(fn));
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  function getParam(name) {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(name);
    if (!raw) return "";
    try { return decodeURIComponent(raw); } catch { return raw; }
  }

  function decodeSafe(v) {
    try { return decodeURIComponent(v); } catch { return v; }
  }

  window.TRUtils = {
    log,
    schedule,
    onReady,
    hookHistory,
    observeDOM,
    getParam,
    decodeSafe
  };
})();


/* ==========================================================================
   SYMBOL WRAPPING (™ / ®)
   - Wraps symbols in span for styling control
   - Applies to headings + hero titles
   ========================================================================== */
(function () {
  const SYMBOL_CLASS = {
    "™": "tm-mark",
    "®": "reg-mark"
  };

  function wrapSymbols(el) {
    if (!el || el.querySelector(".tm-mark, .reg-mark")) return;

    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];

    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach(node => {
      let text = node.nodeValue;
      if (!text) return;

      const symbols = Object.keys(SYMBOL_CLASS);
      if (!symbols.some(s => text.includes(s))) return;

      const frag = document.createDocumentFragment();

      while (text.length) {
        let index = -1, sym = null;

        for (const s of symbols) {
          const i = text.indexOf(s);
          if (i !== -1 && (index === -1 || i < index)) {
            index = i;
            sym = s;
          }
        }

        if (index === -1) {
          frag.appendChild(document.createTextNode(text));
          break;
        }

        if (index > 0) {
          frag.appendChild(document.createTextNode(text.slice(0, index)));
        }

        const span = document.createElement("span");
        span.className = SYMBOL_CLASS[sym];
        span.textContent = sym;
        frag.appendChild(span);

        text = text.slice(index + sym.length);
      }

      node.parentNode.replaceChild(frag, node);
    });
  }

  function run() {
    document
      .querySelectorAll("h1,h2,h3,h4,h5,h6,.hero__title,.section-header__title")
      .forEach(wrapSymbols);
  }

  TRUtils.onReady(run);
  TRUtils.hookHistory(run);
  TRUtils.observeDOM(run);
})();


/* ==========================================================================
   COLLECTION COPY SWAP (Image-with-text section)
   - Changes heading + paragraph based on color filter
   - Trigger: ?filter.v.t.shopify.color-pattern=
   - Applies only on collection pages
   ========================================================================== */
(function () {

  const COPY_BY_GID = {
    "gid://shopify/TaxonomyValue/12": {
      heading: "THE COASTAL MIST COLLECTION",
      body: "Two soft blue tones come together to create a light, airy coastal palette that feels calm, open, and effortlessly relaxed outdoors."
    },
    "gid://shopify/TaxonomyValue/14": {
      heading: "THE DESERT OASIS COLLECTION",
      body: "Soft sun-washed yellow pairs with breezy sea-glass blue for a light, uplifting palette inspired by slow mornings, warm sunshine, and easy days outdoors."
    },
    "gid://shopify/TaxonomyValue/2": {
      heading: "THE BLUE LAGOON COLLECTION",
      body: "Vibrant lagoon blue pairs with deep ocean tones to create a refreshing, energizing palette that feels crisp, cool, and endlessly inviting—like stepping into clear water on a perfect day."
    },
    "gid://shopify/TaxonomyValue/1": {
      heading: "THE OBSIDIAN PEAK COLLECTION",
      body: "Deep charcoal paired with cool light gray creates a clean, understated palette made for early mornings, late nights, and everything from the sideline to the summit."
    },
    "gid://shopify/TaxonomyValue/13": {
      heading: "THE RED ROCK COLLECTION",
      body: "Rich, sun-baked crimson meets deep earthy tones to create a warm, grounded palette inspired by glowing canyon walls, desert sunsets, and the spirit of the outdoors."
    }
  };

  function findSection() {
    const sections = document.querySelectorAll(".feature-row-wrapper, .feature-row");

    for (const s of sections) {
      const root = s.closest(".shopify-section") || s;
      if (root.querySelector(".feature-row__text h2") &&
          root.querySelector(".feature-row__text .rte")) {
        return root;
      }
    }
    return null;
  }

  function run() {
    if (!document.body.classList.contains("template-collection")) return;

    const gid = TRUtils.getParam("filter.v.t.shopify.color-pattern");
    if (!gid || !COPY_BY_GID[gid]) return;

    const section = findSection();
    if (!section) return;

    const h = section.querySelector(".feature-row__text h2");
    const p = section.querySelector(".feature-row__text .rte p");

    if (h) h.textContent = COPY_BY_GID[gid].heading;
    if (p) p.textContent = COPY_BY_GID[gid].body;
  }

  TRUtils.onReady(run);
  TRUtils.hookHistory(run);
  TRUtils.observeDOM(run);
})();

/* ==========================================================================
   COLLECTION IMAGE SWAP (Advanced / Custom Content)
   - Swaps image based on color filter
   - Uses robust selector targeting
   ========================================================================== */
(function () {

  const IMAGES = {
    "gid://shopify/TaxonomyValue/12": "https://cdn.shopify.com/s/files/1/0597/2875/6929/files/coastal-mist-product-materials-flatlay-sketch.jpg?v=1770917427",
    "gid://shopify/TaxonomyValue/14": "https://cdn.shopify.com/s/files/1/0597/2875/6929/files/desert-oasis-product-materials-flatlay-sketch.jpg?v=1770917427",
    "gid://shopify/TaxonomyValue/2":  "https://cdn.shopify.com/s/files/1/0597/2875/6929/files/blue-lagoon-product-materials-flatlay-sketch.jpg?v=1770917427",
    "gid://shopify/TaxonomyValue/1":  "https://cdn.shopify.com/s/files/1/0597/2875/6929/files/obsidian-peak-product-materials-flatlay-sketch.jpg?v=1770917427",
    "gid://shopify/TaxonomyValue/13": "https://cdn.shopify.com/s/files/1/0597/2875/6929/files/red-rock-product-materials-flatlay-sketch.jpg?v=1770917427"
  };

  function findWrap() {
    const candidates = document.querySelectorAll(
      [
        '.custom-content .custom__item--image .image-wrap',
        '.custom-content .custom__item-inner--image .image-wrap',
        '.custom__item--image .image-wrap',
        '.custom__item-inner--image .image-wrap'
      ].join(",")
    );

    return candidates.length ? candidates[0] : null;
  }

  function run() {
    if (!document.body.classList.contains("template-collection")) return;

    const gid = TRUtils.getParam("filter.v.t.shopify.color-pattern");
    if (!gid || !IMAGES[gid]) return;

    const wrap = findWrap();
    if (!wrap) return;

    let img = wrap.querySelector(".tm-swap");
    if (!img) {
      img = document.createElement("img");
      img.className = "tm-swap";
      wrap.appendChild(img);
    }

    img.src = IMAGES[gid];

    Object.assign(img.style, {
      position: "absolute",
      inset: "0",
      width: "100%",
      height: "100%",
      objectFit: "cover",
      zIndex: "2"
    });
  }

  TRUtils.onReady(run);
  TRUtils.hookHistory(run);
  TRUtils.observeDOM(run);
})();


/* ==========================================================================
   COLLECTION COLOR FILTER — SINGLE SELECT
   - Prevents brief “both selected” flash by intercepting early
   - Scoped to template-collection + color-pattern facet only
   ========================================================================== */
(function () {
  const PARAM = "filter.v.t.shopify.color-pattern";

  function isCollection() {
    return document.body.classList.contains("template-collection");
  }

  function decodeSafe(v) {
    try { return decodeURIComponent(v); } catch (e) { return v; }
  }

  function hardUncheckAllColorInputs() {
    const inputs = document.querySelectorAll('input[name="' + PARAM + '"]');
    inputs.forEach(function (input) {
      input.checked = false;
    });
  }

  function getCurrentValue() {
    const url = new URL(window.location.href);
    const v = url.searchParams.get(PARAM);
    return v ? decodeSafe(v) : "";
  }

  function buildUrlWithSingleValue(valueOrBlank) {
    const url = new URL(window.location.href);
    url.searchParams.delete(PARAM);

    if (valueOrBlank) {
      url.searchParams.set(PARAM, valueOrBlank);
    }

    return url.toString();
  }

  function extractClickedValue(el) {
    if (!el) return "";

    const a = el.closest('a[href*="' + PARAM + '="]');
    if (a) {
      try {
        const u = new URL(a.getAttribute("href"), window.location.origin);
        const v = u.searchParams.get(PARAM);
        return v ? decodeSafe(v) : "";
      } catch (e) {}
    }

    let input = el.closest("label")
      ? el.closest("label").querySelector('input[name="' + PARAM + '"]')
      : null;

    if (!input) {
      input = el.closest('input[name="' + PARAM + '"]');
    }

    if (input && input.value) return decodeSafe(input.value);

    const dv = el.getAttribute("data-value") || el.getAttribute("data-facet-value");
    if (dv) return decodeSafe(dv);

    return "";
  }

  function shouldHandleEventTarget(el) {
    return !!el.closest(
      ".collection-filters, .collection-sidebar, .collection-filter, .filter, .facets, .facets__form, .sidebar"
    );
  }

  function intercept(e) {
    if (!isCollection()) return;
    if (!shouldHandleEventTarget(e.target)) return;

    const clickedValue = extractClickedValue(e.target);
    if (!clickedValue) return;

    const current = getCurrentValue();
    const isSame = current && current === clickedValue;

    e.preventDefault();
    e.stopPropagation();

    hardUncheckAllColorInputs();

    const nextUrl = buildUrlWithSingleValue(isSame ? "" : clickedValue);
    window.location.assign(nextUrl);
  }

  document.addEventListener("pointerdown", intercept, true);
  document.addEventListener("click", intercept, true);
})();

/* ==========================================================================
   PDP VARIANT MEDIA FLASH FIX
   - Hides gallery before first paint on ?variant= URLs
   - Reveals sooner once the correct variant is selected
   ========================================================================== */
(function () {
  function getVariantIdFromUrl() {
    var params = new URLSearchParams(window.location.search);
    return params.get("variant") || "";
  }

  function isProductPage() {
    return document.body.classList.contains("template-product");
  }

  function revealProductMedia() {
    document.documentElement.classList.remove("tr-product-variant-preload");
    document.body.classList.remove("tr-variant-loading");
  }

  function currentVariantMatchesUrl() {
    var variantId = getVariantIdFromUrl();
    if (!variantId) return true;

    var selectors = [
      'form[action*="/cart/add"] [name="id"][value="' + variantId + '"]',
      'input[name="id"][value="' + variantId + '"]',
      'select[name="id"] option[value="' + variantId + '"]:checked',
      '[name="id"][value="' + variantId + '"]'
    ];

    for (var i = 0; i < selectors.length; i++) {
      if (document.querySelector(selectors[i])) return true;
    }

    return false;
  }

  function waitForCorrectVariantThenReveal() {
    if (!isProductPage()) return;

    var variantId = getVariantIdFromUrl();
    if (!variantId) {
      revealProductMedia();
      return;
    }

    var tries = 0;
    var maxTries = 60;

    function check() {
      tries += 1;

      if (currentVariantMatchesUrl()) {
        setTimeout(revealProductMedia, 5);
        return;
      }

      if (tries >= maxTries) {
        revealProductMedia();
        return;
      }

      requestAnimationFrame(check);
    }

    requestAnimationFrame(check);
  }

  document.addEventListener("DOMContentLoaded", waitForCorrectVariantThenReveal);
  window.addEventListener("load", waitForCorrectVariantThenReveal);
})();

/* ==========================================================================
   NAV — DISABLE OUTDOOR YOUR WAY PARENT PAGE RESET
   - Desktop split-nav uses <summary data-link="#">
   - Prevents page reset / jump-to-top
   - Keeps dropdown toggle working
   ========================================================================== */
(function () {
  function isOutdoorYourWaySummary(el) {
    if (!el) return false;

    const summary = el.closest('summary.site-nav__link--has-dropdown');
    if (!summary) return false;

    const text = (summary.textContent || "").replace(/\s+/g, " ").trim();
    return text.indexOf("Outdoor Your Way") !== -1;
  }

  function markOutdoorYourWay() {
    const summaries = document.querySelectorAll(
      '.site-nav__details > summary.site-nav__link--has-dropdown'
    );

    summaries.forEach(function (summary) {
      const text = (summary.textContent || "").replace(/\s+/g, " ").trim();

      if (text.indexOf("Outdoor Your Way") !== -1) {
        summary.setAttribute("data-link", "");
        summary.classList.add("tr-nav-no-link");
      }
    });
  }

  function handleOutdoorYourWayClick(e) {
    if (!isOutdoorYourWaySummary(e.target)) return;

    const summary = e.target.closest('summary.site-nav__link--has-dropdown');
    const details = summary ? summary.closest('details.site-nav__details') : null;
    if (!summary || !details) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") {
      e.stopImmediatePropagation();
    }

    // Manually toggle details so dropdown still works
    details.open = !details.open;
    summary.setAttribute("aria-expanded", details.open ? "true" : "false");
  }

  function handleOutdoorYourWayKeydown(e) {
    if (!isOutdoorYourWaySummary(e.target)) return;
    if (e.key !== "Enter" && e.key !== " ") return;

    const summary = e.target.closest('summary.site-nav__link--has-dropdown');
    const details = summary ? summary.closest('details.site-nav__details') : null;
    if (!summary || !details) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") {
      e.stopImmediatePropagation();
    }

    details.open = !details.open;
    summary.setAttribute("aria-expanded", details.open ? "true" : "false");
  }

  document.addEventListener("DOMContentLoaded", markOutdoorYourWay);
  window.addEventListener("load", markOutdoorYourWay);

  // Capture phase so this runs before theme click handlers
  document.addEventListener("click", handleOutdoorYourWayClick, true);
  document.addEventListener("keydown", handleOutdoorYourWayKeydown, true);
})();

/* ==========================================================================
   TENT SLIDESHOW — INIT FLASH FIX
   - Prevents Flickity fade slider from flashing multiple slides on load
   - Applies only to the tent homepage slideshow
   ========================================================================== */
(function () {
  const SECTION_ID = "shopify-section-template--24365419593921__slideshow_TiVpGC";

  function getSection() {
    return document.getElementById(SECTION_ID);
  }

  function getHero(section) {
    if (!section) return null;
    return section.querySelector(".hero");
  }

  function hideUntilReady() {
    const section = getSection();
    const hero = getHero(section);
    if (!hero) return;

    hero.classList.add("tr-slideshow-preload");
  }

  function revealWhenReady() {
    const section = getSection();
    const hero = getHero(section);
    if (!hero) return;

    let tries = 0;
    const maxTries = 120;

    function check() {
      tries += 1;

      const selected = hero.querySelector(".slideshow__slide.is-selected");
      const viewport = hero.querySelector(".flickity-viewport");
      const slider = hero.querySelector(".flickity-slider");

      if (selected && viewport && slider) {
        hero.classList.remove("tr-slideshow-preload");
        return;
      }

      if (tries >= maxTries) {
        hero.classList.remove("tr-slideshow-preload");
        return;
      }

      requestAnimationFrame(check);
    }

    requestAnimationFrame(check);
  }

  document.addEventListener("DOMContentLoaded", function () {
    hideUntilReady();
    revealWhenReady();
  });

  window.addEventListener("load", revealWhenReady);
})();

window.addEventListener("load", function () {
  document.documentElement.classList.remove("tr-preload");
});